import classnames from 'classnames'
import ColumnFilter from 'ColumnFilter'
import ColumnHeader from 'ColumnHeader'
import ColumnSort from 'ColumnSort'
import SortDown from 'icons/SortDown'
import SortUp from 'icons/SortUp'
import {
  isBoolean,
  isEmpty,
  isFunction,
  isNumber,
  isObject,
  isString,
  mapValues,
  some
} from 'lodash'
import PropTypes from 'prop-types'
import React, { ChangeEvent } from 'react'
import { CSVLink } from 'react-csv/lib'
import { AutoSizer, GridCellProps, Index, MultiGrid } from 'react-virtualized'
import ShowHideColumns from 'ShowHideColumns'
import {
  CheckboxItem,
  Column,
  ColumnSet,
  FilterConfig,
  FilterStateSet,
  InputType,
  ResizeInfo,
  Row,
  SelectOptionConfig,
  ShowHideColumnsConfig,
  SortIconsConfig,
  SortState
} from 'types'

// tslint:disable-next-line no-import-side-effect
import './index.css'
import styles from './styles.module.css'

export interface IProps {
  columns: ColumnSet
  enableCsvExport?: boolean
  csvLinkText?: string
  csvFilename?: string
  defaultColumnWidth?: number
  defaultDateFormat?: string
  defaultHeaderHeight?: number
  defaultRowHeight?: number
  defaultSortColumn?: string
  defaultSortAscending?: boolean
  dragHandleIcon?: JSX.Element
  enableClearFilters?: boolean
  enableColumnFilters?: boolean
  enableColumnSort?: boolean
  enableColumnResize?: boolean
  enableSortIcons?: boolean
  enableStripedRows?: boolean
  height?: number
  maxColumnWidth?: number
  minColumnWidth?: number
  onColumnResized?: (columnKey: string, newWidth: number) => void
  onColumnWidthsUpdated?: (columnWidths: object) => void
  onResizeStart?: (resizeInfo: ResizeInfo) => void
  onResizeEnd?: (resizeInfo: ResizeInfo) => void
  onColumnHeaderFilterChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    columnKey: string
  ) => void
  onColumnHeaderLabelClick?: (
    event: ChangeEvent<HTMLElement>,
    columnKey: string
  ) => void
  onFilteredRowsChange?: (rows: Row[], filters: FilterStateSet) => void
  rows?: Row[]
  showHideColumns?: ShowHideColumnsConfig
  sortIcons?: SortIconsConfig
  width?: number
  onCellClicked?: (event: React.MouseEvent<HTMLElement>) => void
}

/**
 * Implement proptypes for js users of the public API.
 * Motivation for the use of propTypes is runtime type safety for non-typescript projects importing this project.
 *
 * This Props class serves to tie together js propTypes the ts interface.
 *
 * When an instance of the class is created it will work as a Component.propTypes definition.
 * TS projects use the ts IProps interface and don't need to care about propTypes.
 *
 * Example of adding prop:
 *
 * static <prop name> = <PropTypes.propType> as any as <TS type>
 * static label = PropTypes.string as any as string
 */
class Props implements IProps {
  columns = (PropTypes.object.isRequired as any) as ColumnSet
  static enableCsvExport = (PropTypes.bool as any) as boolean
  static csvLinkText = (PropTypes.string as any) as string
  static csvFilename = (PropTypes.string as any) as string
  static defaultColumnWidth = (PropTypes.number as any) as number
  static defaultDateFormat = (PropTypes.string as any) as string
  static defaultHeaderHeight = (PropTypes.number as any) as number
  static defaultRowHeight = (PropTypes.number as any) as number
  static defaultSortColumn = (PropTypes.string as any) as string
  static defaultSortAscending = (PropTypes.bool as any) as boolean
  static dragHandleIcon = (PropTypes.element as any) as JSX.Element
  static enableClearFilters = (PropTypes.bool as any) as boolean
  static enableColumnFilters = (PropTypes.bool as any) as boolean
  static enableColumnSort = (PropTypes.bool as any) as boolean
  static enableColumnResize = (PropTypes.bool as any) as boolean
  static enableSortIcons = (PropTypes.bool as any) as boolean
  static enableStripedRows = (PropTypes.bool as any) as boolean
  static height = (PropTypes.number as any) as number
  static maxColumnWidth = (PropTypes.number as any) as number
  static minColumnWidth = (PropTypes.number as any) as number
  static onColumnResized = (PropTypes.func as any) as (
    columnKey: string,
    newWidth: number
  ) => void

  static onColumnWidthsUpdated = (PropTypes.func as any) as (
    columnWidths: object
  ) => void

  static onResizeStart = (PropTypes.func as any) as (
    resizeInfo: ResizeInfo
  ) => void

  static onResizeEnd = (PropTypes.func as any) as (
    resizeInfo: ResizeInfo
  ) => void

  static onColumnHeaderFilterChange = (PropTypes.func as any) as (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    columnKey: string
  ) => void

  static onCellClicked = (PropTypes.func as any) as (
    event: React.MouseEvent<HTMLElement>
  ) => void

  static onColumnHeaderLabelClick = (PropTypes.func as any) as (
    event: ChangeEvent<HTMLElement>,
    columnKey: string
  ) => void

  static onFilteredRowsChange = (PropTypes.func as any) as (
    rows: Row[],
    filters: FilterStateSet
  ) => void

  static rows = (PropTypes.arrayOf(PropTypes.object) as any) as Row[]
  static showHideColumns = (PropTypes.shape({
    localStorageNamespace: PropTypes.string.isRequired,
    menuTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    menuWrapper: PropTypes.func
  }) as any) as ShowHideColumnsConfig

  static sortIcons = (PropTypes.object as any) as SortIconsConfig
  static width = (PropTypes.number as any) as number
}

export type State = {
  columns: ColumnSet
  columnWidths: object
  resizing: boolean
  rows: Row[]
  selectedColumn: string
  sort: SortState
  filters: FilterStateSet
}

const DEFAULT_ROW_HEIGHT = 28
const DEFAULT_HEADER_HEIGHT = 75

class ApexTable extends React.PureComponent<IProps, State> {
  static propTypes = new Props() as IProps

  static defaultProps = {
    csvLinkText: 'export to csv',
    csvFilename: 'table.csv',
    defaultColumnWidth: 150,
    defaultHeaderHeight: DEFAULT_HEADER_HEIGHT,
    defaultRowHeight: DEFAULT_ROW_HEIGHT,
    enableSortIcons: true,
    enableStripedRows: true,
    height: -1,
    maxColumnWidth: 500,
    minColumnWidth: 50,
    rows: [],
    sortIcons: {
      ascending: <SortUp />,
      descending: <SortDown />
    },
    width: -1
  }

  grid?: MultiGrid | null
  indexToColumnKeys!: Record<string, string>
  headerRow!: Record<string, string>
  columnKeys!: string[]

  static makeCSVHeaders(columns: ColumnSet) {
    return Object.keys(columns).map((key) => ({
      key,
      label: columns[key].label
    }))
  }

  static filterOptions(column: Column, columnKey: string, rows: Row[]) {
    // disable any select options that do not exist in rows.
    if (column.options === undefined) return []

    return column.options.map((option: SelectOptionConfig) => {
      const isExtant = rows.some(
        (row) =>
          String(row[columnKey]).toLowerCase() ===
          String(option.value).toLowerCase()
      )
      return { ...option, disabled: !isExtant }
    })
  }

  /**
   * Gets the column visibility from localStorage.
   * Defaults to false if nothing is saved in localStorage for the specified columnKey.
   */
  static getColumnVisibility(
    columnKey: string,
    localStorageNamespace: string,
    defaultVisibility: boolean = true
  ): boolean {
    const storedColumnConfig = localStorage.getItem(localStorageNamespace)

    // Return default if nothing is stored in localStorage for this key.
    if (!storedColumnConfig) return defaultVisibility

    const columns = JSON.parse(storedColumnConfig)

    // Return default if we haven't saved visibility for this column.
    if (!columns || !columns[columnKey]) return defaultVisibility

    const visibility = columns[columnKey].isVisible

    if (visibility === undefined) {
      return defaultVisibility
    }

    return visibility
  }

  /**
   * Sets the column visibility in localStorage.
   */
  static setColumnVisibility(
    columnKey: string,
    localStorageNamespace: string,
    isVisible: boolean
  ) {
    const columns = {
      ...JSON.parse(localStorage.getItem(localStorageNamespace) || '{}')
    }
    columns[columnKey] = { ...columns[columnKey], isVisible }
    localStorage.setItem(localStorageNamespace, JSON.stringify(columns))
  }

  static getRowHeight(
    index: number,
    enableColumnFilters: boolean,
    rows: Row[],
    defaultRowHeight: number = DEFAULT_ROW_HEIGHT,
    defaultHeaderHeight: number = DEFAULT_HEADER_HEIGHT
  ) {
    let height = defaultRowHeight

    if (rows.length > index && rows[index]['numberOfLines']) {
      // @ts-ignore
      height *= parseInt(rows[index]['numberOfLines'], 10)
    } else if (enableColumnFilters && index === 0) {
      height = defaultHeaderHeight
    } else if (index === 1) {
      height = 1
    }
    return height
  }

  constructor(props: IProps) {
    super(props)
    this.getColumnWidth = this.getColumnWidth.bind(this)
    this.handleResizeStart = this.handleResizeStart.bind(this)
    this.handleResizeEnd = this.handleResizeEnd.bind(this)
    this.handleColumnHeaderLabelClick = this.handleColumnHeaderLabelClick.bind(
      this
    )
    this.handleColumnHeaderFilterChange = this.handleColumnHeaderFilterChange.bind(
      this
    )
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this)
    this.sortRows = this.sortRows.bind(this)
    this.clearFilters = this.clearFilters.bind(this)
    this.clearFilter = this.clearFilter.bind(this)

    const columns = { ...this.props.columns }

    if (this.props.showHideColumns) {
      const { localStorageNamespace } = this.props.showHideColumns
      // set visibility from localStorage if the user is using the show/hide columns feature.
      Object.keys(this.props.columns).forEach((columnKey) => {
        columns[columnKey] = {
          ...this.props.columns[columnKey],
          isVisible: ApexTable.getColumnVisibility(
            columnKey,
            localStorageNamespace,
            columns[columnKey].isVisible
          )
        }
      })
    }

    this.updateColumnData(columns)

    const state: State = {
      columns,
      columnWidths: {},
      resizing: false,
      selectedColumn: '',
      filters: mapValues(columns, () => ({ value: '' })),
      rows: [],
      sort: { ascending: false, column: '' }
    }

    let rows = this.props.rows || []
    const { defaultSortAscending, defaultSortColumn } = props

    if (props.enableColumnSort && defaultSortAscending && defaultSortColumn) {
      const sortState: SortState = {
        ascending: defaultSortAscending,
        column: defaultSortColumn
      }
      rows = this.sortRows(sortState, rows)
      state.sort = sortState
    }

    state.rows = rows
    this.state = state
  }

  componentDidUpdate(prevProps: IProps, prevState: State) {
    if (prevState.columns !== this.state.columns) {
      this.updateColumnData(this.state.columns)
      // if column visibility change, we need to call recomputeGridSize, otherwise columns do not line up
      if (this.grid) {
        this.grid.recomputeGridSize()
      }
      this.forceUpdate()
    }

    if (
      // check if we should apply filters and/or sorting
      prevState.filters !== this.state.filters ||
      prevState.sort !== this.state.sort ||
      prevProps.rows !== this.props.rows
    ) {
      let rows = this.props.rows || []
      if (this.props.enableColumnFilters) {
        rows = ColumnFilter.filterRows(this.state.filters, this.props.rows)
        if (this.props.onFilteredRowsChange) {
          // Call the handler so user can get info about the filtered rows.
          // This can be used for something like displaying count of filtered rows.
          this.props.onFilteredRowsChange(rows, { ...this.state.filters })
        }
      }
      if (this.props.enableColumnSort) {
        rows = this.sortRows(this.state.sort, rows)
      }
      /* eslint-disable react/no-did-update-set-state */
      this.setState({ rows })
      /* eslint-enable react/no-did-update-set-state */
    }
  }

  getColumnWidth(columnInfo: Index | string = '') {
    let columnKey
    if (isString(columnInfo)) {
      columnKey = columnInfo
    } else {
      columnKey = this.indexToColumnKeys[columnInfo.index]
    }
    if (columnKey) {
      if (columnKey in this.state.columnWidths) {
        return this.state.columnWidths[columnKey]
      }
      return this.props.columns[columnKey].width
    }

    return this.props.defaultColumnWidth
  }

  getFilterConfig(
    column: Column,
    columnKey: string,
    rows: Row[]
  ): FilterConfig | undefined {
    if (!this.props.enableColumnFilters) return

    const filter: FilterConfig = {
      disabled: column.disabled,
      placeholder: column.placeholder,
      type: column.inputType || InputType.Input,
      // @ts-ignore
      onChange: this.handleColumnHeaderFilterChange,
      options: column.options,
      value: this.state.filters[columnKey].value,
      clear: this.clearFilter
    }

    if (column.options && column.inputType === InputType.Select) {
      // only display select options that exist in rows.
      filter.options = ApexTable.filterOptions(column, columnKey, rows)
    }
    return filter
  }

  clearFilter(columnKey: string) {
    this.setState({
      filters: {
        ...this.state.filters,
        [columnKey]: { value: '' }
      }
    })
  }

  clearFilters() {
    const filters: FilterStateSet = mapValues(this.state.filters, () => ({
      value: ''
    }))
    this.setState({ filters })
  }

  selectSortIcon(columnKey: string): JSX.Element | undefined {
    let sortIcon
    if (
      this.props.enableSortIcons &&
      this.props.sortIcons &&
      this.state.sort.column === columnKey
    ) {
      const { ascending, descending } = this.props.sortIcons
      sortIcon = this.state.sort.ascending ? ascending : descending
    }
    return sortIcon
  }

  sortRows(sort: SortState, rows: Row[]): Row[] {
    let sortedRows = rows
    const columnKey = sort.column || Object.keys(this.props.columns)[0]
    const column = this.props.columns[columnKey]
    if (column) {
      const sortable = isBoolean(column.sortable) ? column.sortable : true
      if (sortable) {
        sortedRows = ColumnSort.sort(
          rows,
          columnKey,
          sort.ascending,
          column.sort,
          this.props.defaultDateFormat
        )
      }
    }
    return sortedRows
  }

  handleResizeStart(resizeInfo: ResizeInfo): void {
    this.setState({ resizing: true }, () => {
      if (this.props.onResizeStart) {
        this.props.onResizeStart(resizeInfo)
      }
    })
  }

  handleResizeEnd(resizeInfo: ResizeInfo): void {
    const columnWidths = {}

    if (this.props.onResizeEnd) {
      this.props.onResizeEnd(resizeInfo)
    } else if (this.props.maxColumnWidth && this.props.minColumnWidth) {
      const currentWidth = this.getColumnWidth(resizeInfo.columnKey)
      const delta = resizeInfo.data.x
      const newWidth = Math.min(
        this.props.maxColumnWidth,
        Math.max(this.props.minColumnWidth, currentWidth + delta)
      )
      if (isNumber(newWidth)) {
        if (this.props.onColumnResized) {
          // client is handling column resize
          this.props.onColumnResized(resizeInfo.columnKey, newWidth)
        } else {
          // default column resizing impl
          columnWidths[resizeInfo.columnKey] = newWidth
        }
      }
    }
    this.setState({ columnWidths, resizing: false }, () => {
      if (this.props.onColumnWidthsUpdated) {
        this.props.onColumnWidthsUpdated(columnWidths)
      }
      if (this.grid) {
        this.grid.recomputeGridSize()
      }
    })
  }

  handleCheckboxChange(event: ChangeEvent<HTMLInputElement>): void {
    const { checked } = event.target
    const columnKey = event.target.getAttribute('data-item-key')
    if (!columnKey) return
    // create a new columns object so that the table detects the prop change
    const columns = { ...this.state.columns }
    columns[columnKey].isVisible = checked
    this.setState({ columns }, () => {
      if (this.props.showHideColumns) {
        ApexTable.setColumnVisibility(
          columnKey,
          this.props.showHideColumns.localStorageNamespace,
          checked
        )
      }
    })
  }

  handleColumnHeaderFilterChange(event: ChangeEvent<HTMLInputElement>): void {
    // Guard against non-object elements.
    if (!isObject(event)) return
    event.stopPropagation()
    event.preventDefault()
    const columnKey = event.target.getAttribute('data-column-key')
    // Guard against invalid columnKeys.
    if (!(isString(columnKey) && columnKey.length > 0)) return

    if (this.props.onColumnHeaderFilterChange) {
      this.props.onColumnHeaderFilterChange(event, columnKey)
    }
    const column = this.state.columns[columnKey]
    // Use the filter provided by the user if they provided a function.
    let { filter } = column
    if (!isFunction(filter)) {
      filter = ColumnFilter.selectFilter(filter)
    }
    // add the filter to state
    this.setState({
      filters: {
        ...this.state.filters,
        [columnKey]: {
          filter,
          value: event.target.value
        }
      }
    })
  }

  // TODO: add type safety to this event.
  handleColumnHeaderLabelClick(e: any): void {
    // Guard resizing and falsy event
    if (this.state.resizing || !e) return
    e.stopPropagation()
    e.preventDefault()
    // Guard non-object target
    if (!isObject(e.target)) return
    const columnKey = e.target.getAttribute('data-column-key')
    // Guard invalid columnKey
    if (!(isString(columnKey) && columnKey.length > 0)) return

    if (this.props.onColumnHeaderLabelClick) {
      this.props.onColumnHeaderLabelClick(e, columnKey)
    }

    const state: any = {
      selectedColumn: columnKey
    }

    // Toggle sort direction if we are still sorting the same column as before.
    if (this.props.enableColumnSort) {
      // Initial sort of a column comes from props, and defaults to false if no prop is provided.
      let ascending = this.props.defaultSortAscending || false
      if (this.state.sort.column === columnKey) {
        ascending = !(isBoolean(this.state.sort.ascending)
          ? this.state.sort.ascending
          : false)
      }
      const sortState = { column: columnKey, ascending }
      const sortedRows = this.sortRows(sortState, this.state.rows)
      state.sort = { ...sortState }
      state.rows = sortedRows
    }
    if (this.grid) {
      this.grid.recomputeGridSize()
    }
    this.setState(state)
  }

  updateColumnData(columns: ColumnSet): void {
    this.indexToColumnKeys = {}
    this.headerRow = {}
    this.columnKeys = Object.keys(columns).filter(
      (columnKey) => columns[columnKey].isVisible
    )
    this.columnKeys.forEach((key, i) => {
      this.indexToColumnKeys[i] = key
      this.headerRow[key] = key
    })
  }

  renderCell(rows: Row[]) {
    return ({
      columnIndex,
      key,
      rowIndex,
      style
    }: GridCellProps): JSX.Element => {
      let cell
      const columnKey = this.indexToColumnKeys[columnIndex]
      const column = this.state.columns[columnKey]
      const rowIndexWithoutColumnHeader = rowIndex - 1
      const unformattedValue =
        rows.length > rowIndex ? rows[rowIndex][columnKey] : ''
      const formattedValue = column.formatter
        ? column.formatter(unformattedValue, rowIndexWithoutColumnHeader)
        : unformattedValue
      // Add a space to the end of cellValue so that double-clicking the element highlights only the text of the cell.
      // But only do so if it is a string. Could be a jsx element.
      const cellValue = isString(formattedValue)
        ? `${formattedValue} `
        : formattedValue
      if (rowIndex === 0) {
        // It's the header row.
        cell = (
          <ColumnHeader
            key={key}
            className={
              this.state.selectedColumn === columnKey ? 'selected' : ''
            }
            dragHandleIcon={this.props.dragHandleIcon}
            sortIcon={this.selectSortIcon(columnKey)}
            columnKey={columnKey}
            filter={this.getFilterConfig(column, columnKey, rows)}
            label={column.label}
            onResizeStart={this.handleResizeStart}
            onResizeEnd={this.handleResizeEnd}
            onLabelClick={this.handleColumnHeaderLabelClick}
            resizable={this.props.enableColumnResize ? column.resizable : false}
            style={style}
            resizeBounds={{
              left: -column.width,
              right: Math.max(200, column.width * 2),
              top: 0,
              bottom: 0
            }}
          />
        )
      } else if (rowIndex === 1) {
        // It's the invisible dummy row.

        // This is used to prevent an issue where when the table re-renders
        // (due to filtering) with no rows aside from the headerRow,
        // filter values are lost and viewport is scrolled all the way left.
        cell = <span key={key} style={{ height: 0 }} />
      } else {
        // NOTE: this cell is intentionally not implemented as a Cell component.
        // The reason being is that the additional lifecycle events negatively affect
        // the performance of react-virtualized
        let stripeClass = 'even'
        if (
          this.props.enableStripedRows &&
          rowIndexWithoutColumnHeader % 2 === 1
        ) {
          stripeClass = 'odd'
        }

        const cellProps: Record<string, any> = {
          key,
          className: classnames(
            'table-cell',
            stripeClass,
            styles.tableCell,
            styles[stripeClass]
          ),
          'data-row-index': rowIndexWithoutColumnHeader,
          'data-test-key': columnKey,
          style: {
            ...style,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textAlign: 'left'
          }
        }

        if (this.props.onCellClicked) {
          cellProps.onClick = this.props.onCellClicked
        }

        cell = <div {...cellProps}>{cellValue}</div>
      }
      return cell
    }
  }

  render() {
    // When the table re-renders (due to filtering) with no rows aside from the headerRow
    // filter values are lost and viewport is scrolled all the way left.
    const rows = [
      this.headerRow,
      {
        /* dummy row */
      },
      ...this.state.rows
    ]
    const cellRenderer = this.renderCell(rows)
    const clearFiltersButtonHeight = this.props.enableClearFilters ? 23 : 0
    const enableColumnFilters = this.props.enableColumnFilters || false
    const tableHeight = this.props.height || 0
    const tableWidth = this.props.width || 0

    let checkboxItems: CheckboxItem[] = []
    // Check if columns are visible
    if (this.props.showHideColumns) {
      checkboxItems = Object.keys(this.state.columns).map((key) => {
        const col = this.state.columns[key]
        return {
          key,
          label: col.label,
          checked: col.isVisible
        }
      })
    }

    const isSomeFilterEnabled = some(
      this.state.filters,
      (filter: { value: string }) => !isEmpty(filter.value)
    )

    return (
      <div className='apex-table-wrapper'>
        <div>
          {this.props.showHideColumns &&
            (this.props.showHideColumns.menuWrapper ? (
              // @ts-ignore
              <this.props.showHideColumns.menuWrapper>
                <ShowHideColumns
                  items={checkboxItems}
                  onCheckboxChange={this.handleCheckboxChange}
                />
              </this.props.showHideColumns.menuWrapper>
            ) : (
              <ShowHideColumns
                items={checkboxItems}
                onCheckboxChange={this.handleCheckboxChange}
              />
            ))}
          {enableColumnFilters && this.props.enableClearFilters && (
            <button
              disabled={!isSomeFilterEnabled}
              className='clear-all-filters'
              onClick={this.clearFilters}
            >
              Clear Filters
            </button>
          )}
          {this.props.enableCsvExport && (
            <CSVLink
              className='csv-link pull-right'
              data={rows.slice(1)}
              headers={ApexTable.makeCSVHeaders(this.state.columns)}
              filename={`${this.props.csvFilename}`}
            >
              {this.props.csvLinkText}
            </CSVLink>
          )}
        </div>
        <AutoSizer>
          {({ height, width }) => (
            <div className='apex-table-container'>
              <MultiGrid
                classNameTopRightGrid={classnames(
                  styles.headerRow,
                  'header-row'
                )}
                cellRenderer={cellRenderer}
                columnCount={this.columnKeys.length}
                columnWidth={this.getColumnWidth}
                enableFixedRowScroll
                estimatedColumnSize={100 * this.columnKeys.length}
                fixedRowCount={1}
                height={
                  (tableHeight > 0 ? tableHeight : height) -
                  clearFiltersButtonHeight
                }
                hideTopRightGridScrollbar
                overscanRowCount={50}
                ref={(r) => {
                  this.grid = r
                  return this.grid
                }}
                rowCount={rows.length}
                rowHeight={({ index }) =>
                  ApexTable.getRowHeight(
                    index,
                    enableColumnFilters,
                    rows,
                    this.props.defaultRowHeight,
                    this.props.defaultHeaderHeight
                  )
                }
                width={tableWidth > 0 ? tableWidth : width}
              />
            </div>
          )}
        </AutoSizer>
      </div>
    )
  }
}

export default ApexTable
export * from './types'
